import { lazy } from "react";

import BUCustomerSuccessPastNotes from "pages/BusinessUnit/PastNotes/CustomerSuccess/View";
import BUFieldSalesPastNotes from "pages/BusinessUnit/PastNotes/FieldSales/View";
import BUUnassignedAccount from "pages/BusinessUnit/UnassignedAccount/View";

import { RouteType } from "types/index";
import { BU_ACCESS_RIGHT, OPSNAVI_ACCESS_RIGHT } from "constants/accessRight";
import {
  buSlugParam,
  corpSlugParam,
  personalSlugParam,
  routePaths,
} from "routes/constants";

const BUMemberProfile = lazy(
  () => import("pages/BusinessUnit/Member/Profile/Main/View")
);
const BUMemberDesiredJobPreferencesProfile = lazy(
  () => import("pages/BusinessUnit/Member/Profile/DesiredJobPreferences/View")
);
const BUMemberWorkHistoryAndOtherProfile = lazy(
  () => import("pages/BusinessUnit/Member/Profile/WorkHistoryAndOther/View")
);
const BUMemberLaborManagementProfile = lazy(
  () => import("pages/BusinessUnit/Member/Profile/LaborManagement/View")
);
const BUMemberCurrentStatusProfile = lazy(
  () => import("pages/BusinessUnit/Member/Profile/CurrentStatus/View")
);
const BUMemberPastStatusProfile = lazy(
  () => import("pages/BusinessUnit/Member/Profile/PastStatus/View")
);

const BUList = lazy(() => import("pages/BusinessUnit/List/View"));

const BUBasicGeneralSetting = lazy(
  () => import("pages/BusinessUnit/GeneralSetting/Basic/View")
);
const BUOtherGeneralSetting = lazy(
  () => import("pages/BusinessUnit/GeneralSetting/OtherSetting/View")
);

const BUEmployeeMember = lazy(
  () => import("pages/BusinessUnit/Member/Employee/View")
);
const BUStaffMember = lazy(
  () => import("pages/BusinessUnit/Member/Staff/View")
);
const BUClientMember = lazy(
  () => import("pages/BusinessUnit/Member/Client/View")
);
const BUCorporationMember = lazy(
  () => import("pages/BusinessUnit/Member/Corporation/View")
);
const StaffAssignedAccount = lazy(
  () => import("pages/BusinessUnit/AssignedAccount/Staff/View")
);
const ClientAssignedAccount = lazy(
  () => import("pages/BusinessUnit/AssignedAccount/Client/View")
);
const CorporationAssignedAccount = lazy(
  () => import("pages/BusinessUnit/AssignedAccount/Corporation/View")
);

const ManageMemberAssignment = lazy(
  () => import("pages/BusinessUnit/ManageMemberAssignment/View")
);
const ManageMemberStaffAssignment = lazy(
  () =>
    import("pages/BusinessUnit/ManageMemberAssignment/AssignedAccount/Staff/View")
);
const ManageMemberClientAssignment = lazy(
  () =>
    import("pages/BusinessUnit/ManageMemberAssignment/AssignedAccount/Client/View")
);
const ManageMemberCorporationAssignment = lazy(
  () =>
    import("pages/BusinessUnit/ManageMemberAssignment/AssignedAccount/Corporation/View")
);

const ActiveVacancies = lazy(
  () => import("pages/BusinessUnit/ManagePostings/ActiveVacancies/View")
);

const ExportWebAds = lazy(
  () =>
    import("pages/BusinessUnit/ManagePostings/ActiveVacancies/ExportWebAds/View")
);

const ExportSummary = lazy(
  () =>
    import("pages/BusinessUnit/ManagePostings/ActiveVacancies/ExportSummary/View")
);

const UnpublishedJobList = lazy(
  () => import("pages/BusinessUnit/ManagePostings/UnpublishedJobList/View")
);

const ManageTags = lazy(() => import("pages/BusinessUnit/ManageTags/View"));

const BUInsideSalesPastNotes = lazy(
  () => import("pages/BusinessUnit/PastNotes/InsideSales/View")
);

const ScorecardHistoryStaff = lazy(
  () => import("pages/BusinessUnit/Evaluate/ScorecardHistory/Staff/View")
);

const ScorecardHistoryJobListingCorporation = lazy(
  () =>
    import("pages/BusinessUnit/Evaluate/ScorecardHistory/JobListingCorporation/View")
);

export const businessUnitRoutes: RouteType[] = [
  {
    path: routePaths.root.bu.root.root,
    element: <BUList />,
    requiredAuth: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.root,
    element: null,
    requiredAuth: true,
    redirect: routePaths.root.bu.root.pBuSlug.generalSetting.root,
    label: buSlugParam,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.generalSetting.root,
    element: <BUBasicGeneralSetting />,
    requiredAuth: true,
    label: "general_settings",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.generalSetting.otherSetting.root,
    element: <BUOtherGeneralSetting />,
    requiredAuth: true,
    hideBreadcrumb: true,
    requiredAccessRights: [BU_ACCESS_RIGHT.LEAVE_BU],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.root,
    element: null,
    requiredAuth: true,
    redirect: routePaths.root.bu.root.pBuSlug.member.employee.root,
    label: "members",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.employee.root,
    element: <BUEmployeeMember />,
    requiredAuth: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.staff.root,
    element: <BUStaffMember />,
    requiredAuth: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.client.root,
    element: <BUClientMember />,
    requiredAuth: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.corporation.root,
    element: <BUCorporationMember />,
    requiredAuth: true,
    hideBreadcrumb: true,
  },

  {
    path: routePaths.root.bu.root.pBuSlug.member.corp.pCorporationSlug.root,
    element: <BUMemberProfile />,
    requireAssignment: true,
    requiredAuth: true,
    label: corpSlugParam,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.pPersonal.root,
    element: <BUMemberProfile />,
    requireAssignment: true,
    requiredAuth: true,
    label: personalSlugParam,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.pPersonal.desiredJobPreferences
      .root,
    element: <BUMemberDesiredJobPreferencesProfile />,
    requiredAuth: true,
    requireAssignment: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.pPersonal.workHistoryAndOther
      .root,
    element: <BUMemberWorkHistoryAndOtherProfile />,
    requiredAuth: true,
    requireAssignment: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.pPersonal.laborManagement.root,
    element: <BUMemberLaborManagementProfile />,
    requiredAuth: true,
    requireAssignment: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.pPersonal.currentStatus.root,
    element: <BUMemberCurrentStatusProfile />,
    requiredAuth: true,
    requireAssignment: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.member.pPersonal.pastStatus.root,
    element: <BUMemberPastStatusProfile />,
    requiredAuth: true,
    requireAssignment: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.managePostings.root,
    element: null,
    requiredAuth: true,
    redirect:
      routePaths.root.bu.root.pBuSlug.managePostings.activeVacancies.root,
    label: "manage_postings",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.managePostings.activeVacancies.root,
    element: <ActiveVacancies />,
    requiredAuth: true,
    requiredAccessRights: [BU_ACCESS_RIGHT.MANAGE_JOB_DOCUMENT],
    label: "active_vacancies",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.managePostings.activeVacancies
      .exportWebAds.root,
    element: <ExportWebAds />,
    requiredAuth: true,
    requiredAccessRights: [BU_ACCESS_RIGHT.MANAGE_JOB_DOCUMENT],
    label: "export_web_advertisements",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.managePostings.activeVacancies
      .exportSummary.root,
    element: <ExportSummary />,
    requiredAuth: true,
    requiredAccessRights: [BU_ACCESS_RIGHT.MANAGE_JOB_DOCUMENT],
    label: "export_job_summary",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.managePostings.unpublished.root,
    element: <UnpublishedJobList />,
    requiredAuth: true,
    requiredAccessRights: [BU_ACCESS_RIGHT.MANAGE_JOB_DOCUMENT],
    label: "unpublished",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.root,
    element: null,
    requiredAuth: true,
    redirect: routePaths.root.bu.root.pBuSlug.assignedAccount.staff.root,
    label: "assigned_accounts",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.staff.root,
    element: <StaffAssignedAccount />,
    requiredAuth: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.client.root,
    element: <ClientAssignedAccount />,
    requiredAuth: true,
    hideBreadcrumb: true,
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.corporation.root,
    element: <CorporationAssignedAccount />,
    requiredAuth: true,
    hideBreadcrumb: true,
  },

  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.manage
      .manageMemberAssignment.root,
    element: <ManageMemberAssignment />,
    requiredAuth: true,
    label: "manage_members_assignment",
    requiredAccessRights: [BU_ACCESS_RIGHT.ASSIGN_STAFF],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.manage
      .manageMemberAssignment.memberAssignment.root,
    element: null,
    requiredAuth: true,
    redirect:
      routePaths.root.bu.root.pBuSlug.assignedAccount.manage
        .manageMemberAssignment.memberAssignment.staff.root,
    label: personalSlugParam,
    requiredAccessRights: [BU_ACCESS_RIGHT.ASSIGN_STAFF],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.manage
      .manageMemberAssignment.memberAssignment.staff.root,
    element: <ManageMemberStaffAssignment />,
    requiredAuth: true,
    hideBreadcrumb: true,
    requiredAccessRights: [BU_ACCESS_RIGHT.ASSIGN_STAFF],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.manage
      .manageMemberAssignment.memberAssignment.client.root,
    element: <ManageMemberClientAssignment />,
    requiredAuth: true,
    hideBreadcrumb: true,
    requiredAccessRights: [BU_ACCESS_RIGHT.ASSIGN_STAFF],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.manage
      .manageMemberAssignment.memberAssignment.corporation.root,
    element: <ManageMemberCorporationAssignment />,
    requiredAuth: true,
    hideBreadcrumb: true,
    requiredAccessRights: [BU_ACCESS_RIGHT.ASSIGN_STAFF],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.assignedAccount.manage
      .unassignedAccount.root,
    element: <BUUnassignedAccount />,
    requiredAuth: true,
    label: "unassigned_accounts",
    requiredAccessRights: [BU_ACCESS_RIGHT.ASSIGN_STAFF],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.manageTags.root,
    element: <ManageTags />,
    requiredAuth: true,
    label: "manage_tags",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.pastNotes.insideSales.root,
    element: <BUInsideSalesPastNotes />,
    requiredAuth: true,
    label: "past_notes",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.pastNotes.fieldSales.root,
    element: <BUFieldSalesPastNotes />,
    requiredAuth: true,
    label: "past_notes",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.pastNotes.customerSuccess.root,
    element: <BUCustomerSuccessPastNotes />,
    requiredAuth: true,
    label: "past_notes",
  },
  {
    path: routePaths.root.bu.root.pBuSlug.scorecardHistory.root,
    element: null,
    requiredAuth: true,
    redirect:
      routePaths.root.bu.root.pBuSlug.scorecardHistory.scorecardHistoryStaff
        .root,
    label: "scorecard_history",
    requiredAccessRights: [
      OPSNAVI_ACCESS_RIGHT.SCORE_CARD_READ_ALL_SCORE_CARDS,
      OPSNAVI_ACCESS_RIGHT.SCORE_CARD_READ_ASSIGNED_SCORE_CARDS,
    ],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.scorecardHistory.scorecardHistoryStaff
      .root,
    element: <ScorecardHistoryStaff />,
    requiredAuth: true,
    hideBreadcrumb: true,
    requiredAccessRights: [
      OPSNAVI_ACCESS_RIGHT.SCORE_CARD_READ_ALL_SCORE_CARDS,
      OPSNAVI_ACCESS_RIGHT.SCORE_CARD_READ_ASSIGNED_SCORE_CARDS,
    ],
  },
  {
    path: routePaths.root.bu.root.pBuSlug.scorecardHistory
      .scorecardHistoryJobListingCorporation.root,
    element: <ScorecardHistoryJobListingCorporation />,
    requiredAuth: true,
    hideBreadcrumb: true,
    requiredAccessRights: [
      OPSNAVI_ACCESS_RIGHT.SCORE_CARD_READ_ALL_SCORE_CARDS,
      OPSNAVI_ACCESS_RIGHT.SCORE_CARD_READ_ASSIGNED_SCORE_CARDS,
    ],
  },
];
